import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { supabase } from '@/lib/supabase';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/Components/ui/card';
import { AlertCircle, Loader2, LogIn } from 'lucide-react';
import SEO from '../Components/SEO.jsx';

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const redirectPath = searchParams.get('redirect') || '/area-clienti';

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const { error: authError } = await supabase.auth.signInWithPassword({
                email,
                password,
            });

            if (authError) throw authError;

            if (authError) throw authError;

            // Check if user is admin
            const { data: { user } } = await supabase.auth.getUser();
            const isAdmin = user?.user_metadata?.role === 'admin' || email === 'admin@tecnosys-ups.com';

            if (isAdmin) {
                navigate('/admin');
            } else {
                navigate(redirectPath);
            }
        } catch (err) {
            setError(err.message || 'Errore durante il login. Controlla le tue credenziali.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center px-4 pt-20">
            <SEO
                title="Login - Area Clienti | Tecnosys"
                description="Accedi alla tua area riservata per monitorare UPS, riparazioni e contratti."
            />

            <Card className="w-full max-w-md">
                <CardHeader className="text-center space-y-2">
                    <div className="w-16 h-16 mx-auto rounded-full bg-blue-100 flex items-center justify-center mb-4">
                        <LogIn className="w-8 h-8 text-blue-600" />
                    </div>
                    <CardTitle className="text-2xl">Accesso Area Clienti</CardTitle>
                    <p className="text-gray-600 text-sm">Inserisci le tue credenziali per accedere</p>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleLogin} className="space-y-4">
                        <div>
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="mt-1"
                                placeholder="nome@azienda.it"
                            />
                        </div>

                        <div>
                            <Label htmlFor="password">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                className="mt-1"
                            />
                        </div>

                        {error && (
                            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
                                <AlertCircle className="w-4 h-4" />
                                {error}
                            </div>
                        )}

                        <Button type="submit" className="w-full rounded-full bg-blue-600 hover:bg-blue-700 text-white" disabled={loading}>
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Accesso in corso...
                                </>
                            ) : (
                                'Accedi'
                            )}
                        </Button>
                    </form>

                    <div className="mt-6 text-center space-y-3">
                        <p className="text-sm text-gray-600">
                            Non hai ancora un account?{' '}
                            <Link to={createPageUrl('Register')} className="text-blue-600 hover:underline font-medium">
                                Registrati
                            </Link>
                        </p>
                        <Link to={createPageUrl('Home')} className="block text-sm text-gray-500 hover:text-gray-700">
                            ← Torna alla home
                        </Link>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
